import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';

const toastVariants = cva(
  'flex items-center justify-between p-4 rounded-md shadow-md border-l-4 max-w-md',
  {
    variants: {
      level: {
        success: 'bg-success-subtle border-success text-success-subtle-foreground',
        info: 'bg-info-subtle border-info text-info-subtle-foreground',
        warning: 'bg-warning-subtle border-warning text-warning-subtle-foreground',
        error: 'bg-destructive-subtle border-destructive text-destructive-subtle-foreground',
      },
    },
    defaultVariants: {
      level: 'info',
    },
  },
);

type Props = {
  id: string;
  message: string;
  onClose: (id: string) => void;
  autoClose: boolean | undefined;
  autoCloseDelay: number | undefined;
} & VariantProps<typeof toastVariants>;

type ToastIconProps = {
  level: VariantProps<typeof toastVariants>['level'];
};

const ToastIcon = ({ level }: ToastIconProps) => {
  if (level === 'success') {
    return <CheckCircle className="h-5 w-5 text-success" />;
  }
  if (level === 'warning') {
    return <AlertTriangle className="h-5 w-5 text-warning" />;
  }
  if (level === 'error') {
    return <AlertCircle className="h-5 w-5 text-destructive" />;
  }
  return <Info className="h-5 w-5 text-info" />;
};

export const Toast: FC<Props> = ({
  id,
  message,
  level = 'info',
  onClose,
  autoClose = true,
  autoCloseDelay = 5000,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  const handleClose = useCallback(() => {
    setIsLeaving(true);
    setTimeout(() => {
      onClose(id);
    }, 300);
  }, [id, onClose]);

  useEffect(() => {
    setTimeout(() => {
      setIsVisible(true);
    }, 10);

    if (autoClose) {
      const timer = setTimeout(() => {
        handleClose();
      }, autoCloseDelay);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [autoClose, autoCloseDelay, handleClose]);

  return (
    <div
      className={`${toastVariants({ level })} transform transition-all duration-300 ease-in-out ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'} ${isLeaving ? 'translate-x-full opacity-0' : ''}`}
      role="alert"
    >
      <div className="flex items-center gap-3">
        <ToastIcon level={level} />
        <p>{message}</p>
      </div>
      <button
        onClick={handleClose}
        className="ml-4 text-muted-foreground hover:text-foreground"
        aria-label="Close notification"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};
